import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import purchaseService from "../../Services/purchaseService";
import MedicineDetailsModal from "../Medicines/MedicineDetailsModal";
import moment from "moment";

const DiscountModal = ({ open, onClose, onApprove, purchaseId }) => {
  const [discount, setDiscount] = useState("");
  const handleSubmit = () => {
    if (isNaN(discount) || parseFloat(discount) < 0) {
      toast.error("Invalid discount.");
      return;
    }
    onApprove(purchaseId, parseFloat(discount));
    onClose();
  };
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-sm overflow-hidden">
        <div className="bg-[#1F2937] px-5 py-4 flex items-center justify-between">
          <p className="text-[13px] font-semibold text-white">Enter Discount</p>
          <button
            onClick={onClose}
            className="text-gray-300 hover:text-white text-xl"
          >
            &times;
          </button>
        </div>
        <div className="px-5 py-4">
          <input
            type="number"
            placeholder="Discount Amount"
            value={discount}
            onChange={(e) => setDiscount(e.target.value)}
            className="w-full text-xs border border-gray-200 rounded-lg px-3 py-2.5 text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-100 mb-4"
          />
          <div className="flex justify-end gap-2">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="text-xs px-3 py-1.5 rounded-lg bg-[#1F2937] text-white hover:bg-gray-700"
            >
              Approve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Purchases = () => {
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [discountModalOpen, setDiscountModalOpen] = useState(false);
  const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      const response = await purchaseService.fetchAll();
      setPurchases(response.purchases);
    } catch (error) {
      toast.error("Error fetching purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const result =
      purchases?.filter((item) =>
        item?.name?.toLowerCase().includes(searchData.toLowerCase()),
      ) || [];
    setFilteredData(result);
  }, [searchData, purchases]);

  const handleApprovePurchase = async (purchaseId, discount) => {
    try {
      await purchaseService.approvePurchase(purchaseId, discount);
      toast.success("Purchase approved!");
      fetchPurchases();
    } catch (error) {
      toast.error("Error approving purchase.");
    }
  };

  const totalAmount = filteredData.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );

  const colWidths = [
    "70px",
    "160px",
    "160px",
    "110px",
    "120px",
    "90px",
    "110px",
  ];
  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  return (
    <div className="p-3 sm:p-4 font-sans">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
            Purchases
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {filteredData.length || 0} records found
          </p>
        </div>
      </div>

      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          />
        </svg>
        <input
          type="search"
          placeholder="Search by supplier name..."
          value={searchData}
          onChange={(e) => setSearchData(e.target.value)}
          className="w-full sm:w-72 pl-9 pr-4 py-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-100"
        />
      </div>

      {/* ══ MOBILE CARDS ══ */}
      <div className="block sm:hidden space-y-2">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            Loading...
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            No purchases found
          </div>
        ) : (
          filteredData.map((purchase, index) => (
            <div
              key={purchase?.id}
              className="border border-gray-200 rounded-xl bg-white px-3 py-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-semibold text-gray-600 uppercase flex-shrink-0">
                    {purchase?.name?.charAt(0) || "S"}
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-gray-800">
                      {purchase?.name}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {purchase?.company}
                    </p>
                  </div>
                </div>
                <span
                  className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${purchase?.approved === 0 ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}
                >
                  {purchase?.approved === 0 ? "Not Approved" : "Approved"}
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {[
                  { label: "Amount", value: purchase?.amount },
                  { label: "Date", value: moment(purchase?.date).format("ll") },
                ].map(({ label, value }) => (
                  <div key={label} className="flex justify-between py-1.5">
                    <span className="text-[11px] text-gray-400 w-16">
                      {label}
                    </span>
                    <span className="text-[11px] text-gray-700 text-right">
                      {value}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between py-1.5">
                  <span className="text-[11px] text-gray-400 w-16">Action</span>
                  <button
                    className="text-blue-500 font-medium text-[11px] hover:underline"
                    onClick={() => navigate(`purchaseview?id=${purchase?.id}`)}
                  >
                    View
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ══ DESKTOP TABLE ══ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />
            <thead>
              <tr>
                {[
                  "Sr No.",
                  "Supplier Name",
                  "Company",
                  "Amount",
                  "Purchase Date",
                  "Action",
                  "Status",
                ].map((label, i) => (
                  <th
                    key={label}
                    className={`sticky top-0 z-20 bg-[#1F2937] text-left px-4 py-2.5 text-[11px] text-gray-100 uppercase font-medium ${i < 6 ? "border-r border-white/10" : ""}`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    No purchases found
                  </td>
                </tr>
              ) : (
                filteredData.map((purchase, index) => (
                  <tr
                    key={purchase?.id}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${index % 2 ? "bg-gray-50" : "bg-white"}`}
                  >
                    <td className="px-4 py-2.5 text-[12px] font-semibold text-gray-500 border-r border-gray-100">
                      {index + 1}
                    </td>
                    <td className="px-4 py-2.5 border-r border-gray-100">
                      <div className="flex items-center gap-2">
                        {/* <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-600 uppercase flex-shrink-0">
                          {purchase?.name?.charAt(0) || "S"}
                        </div> */}
                        <span className="text-[12px] font-medium text-gray-800 truncate">
                          {purchase?.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100 truncate">
                      {purchase?.company}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      {purchase?.amount}
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100">
                      {moment(purchase?.date).format("ll")}
                    </td>
                    <td className="px-4 py-2.5 border-r border-gray-100">
                      <button
                        className="text-[11px] font-medium text-white bg-[#1F2937] px-2.5 py-1 rounded-lg hover:bg-[#111827] transition"
                        onClick={() =>
                          navigate(`purchaseview?id=${purchase?.id}`)
                        }
                      >
                        View
                      </button>
                    </td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${purchase?.approved === 0 ? "bg-red-100 text-red-600" : "bg-green-100 text-green-700"}`}
                      >
                        {purchase?.approved === 0 ? "Not Approved" : "Approved"}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={3}
                  className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] font-medium text-gray-100 border-t border-white/10 border-r"
                >
                  {filteredData.length || 0} total records
                </td>
                <td
                  className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] font-semibold text-[#F07F1C] border-t border-white/10"
                  colSpan={4}
                >
                  Total: {totalAmount.toFixed(2)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── Supplier Ledger ── */}
      <MedicineDetailsModal
        open={modalOpen}
        onClose={() => {
          setSelectedMedicine(null);
          setModalOpen(false);
        }}
        medicine={selectedMedicine}
      />
      <DiscountModal
        open={discountModalOpen}
        onClose={() => {
          setDiscountModalOpen(false);
          setSelectedPurchaseId(null);
        }}
        onApprove={handleApprovePurchase}
        purchaseId={selectedPurchaseId}
      />
    </div>
  );
};

export default Purchases;
